// Mail left sidebar — Naver-style folder tree with categorized sections:
//   메일함 (system folders + counts) · 내 라벨 (color folders) · 외부 메일
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../shared/api/client";
import { I } from "../shared/Icon";
import { toast } from "../shared/stores/toasts";
import { cachedGet, invalidateMailCache } from "../pages/mail/mailCache";

type Counts = {
  RECEIVED: number;
  INBOX: number; ADS: number; SENT: number; DRAFT: number; TRASH: number; SPAM: number;
  UNREAD: number;
  STARRED: number; IMPORTANT: number; SCHEDULED: number; unread_inbox: number;
  unread?: Record<string, number>;
};
type Label = { id: number; name: string; color: string };
type Folder = { id: number; name: string; code: string; sort_order: number; parent_id: number | null; total: number; unread: number };

const FOLDERS: { id: string; label: string; icon: string }[] = [
  { id: "RECEIVED", label: "전체 메일함", icon: "🗂️" },
  { id: "INBOX", label: "받은 메일함", icon: "📥" },
  { id: "UNREAD", label: "안 읽은 메일", icon: "📬" },
  { id: "STARRED", label: "별표 표시", icon: "⭐" },
  { id: "IMPORTANT", label: "중요 메일", icon: "❗" },
  { id: "SENT", label: "보낸 메일함", icon: "📤" },
  { id: "DRAFT", label: "임시 보관함", icon: "📝" },
  { id: "SCHEDULED", label: "예약 발송", icon: "⏰" },
  { id: "ADS", label: "광고 메일함", icon: "📢" },
  { id: "SPAM", label: "스팸함", icon: "🚫" },
  { id: "TRASH", label: "휴지통", icon: "🗑️" },
];

export function MailSidebar({ onCollapse }: { onCollapse: () => void }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const activeFilter = (params.get("filter") || "RECEIVED").toUpperCase();   // MailPage 의 기본값과 같아야 강조가 맞는다
  const activeLabel = params.get("label");

  const [counts, setCounts] = useState<Counts | null>(null);
  const [labels, setLabels] = useState<Label[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [adding, setAdding] = useState(false);
  const [addParentId, setAddParentId] = useState<number | null>(null);   // null = 최상위 추가
  const [newName, setNewName] = useState("");
  const [dragId, setDragId] = useState<number | null>(null);
  const [ctx, setCtx] = useState<{ x: number; y: number; kind: "system" | "label" | "custom"; data: any } | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem("mail-tree-open") || "") || { folders: true, labels: true, custom: true, ext: true }; }
    catch { return { folders: true, labels: true, custom: true, ext: true }; }
  });
  const [expanded, setExpanded] = useState<Record<number, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem("mail-folder-expanded") || "") || {}; }
    catch { return {}; }
  });
  const toggleExpand = (id: number) => setExpanded((s) => {
    const next = { ...s, [id]: !s[id] };
    try { localStorage.setItem("mail-folder-expanded", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  // 같은 부모를 가진 형제 폴더를 정렬 순서대로
  const childrenOf = (parentId: number | null) =>
    folders.filter((f) => (f.parent_id ?? null) === parentId).sort((a, b) => a.sort_order - b.sort_order);
  // 드래그로 형제 순서 변경(같은 부모끼리만)
  const onFolderDrop = async (target: Folder) => {
    const src = dragId; setDragId(null);
    if (src == null || src === target.id) return;
    const dragF = folders.find((f) => f.id === src);
    if (!dragF || (dragF.parent_id ?? null) !== (target.parent_id ?? null)) return;   // 다른 레벨끼리는 무시
    const sibs = childrenOf(target.parent_id ?? null).map((f) => f.id).filter((id) => id !== src);
    const at = sibs.indexOf(target.id);
    sibs.splice(at < 0 ? sibs.length : at, 0, src);
    try { await api.put("/mail/folders/reorder", { ids: sibs }); afterChange(); }
    catch (e: any) { toast.error("순서 변경 실패", e?.response?.data?.error?.message); }
  };

  // 본문 목록도 같은 API 를 부른다. 캐시로 묶어 왕복을 한 번으로 줄인다.
  // (묶기 전 실측: folders 39회 / counts 35회 / labels 28회가 한 화면에서 나갔다)
  const loadCounts = () => cachedGet<Counts>("mail:counts", "/mail/counts").then(setCounts).catch(() => {});
  const loadLabels = () => cachedGet<Label[]>("mail:labels", "/mail/labels/")
    .then((d) => setLabels(Array.isArray(d) ? d : [])).catch(() => {});
  const loadFolders = () => cachedGet<Folder[]>("mail:folders", "/mail/folders/")
    .then((d) => setFolders(Array.isArray(d) ? d : [])).catch(() => {});

  useEffect(() => { loadCounts(); loadLabels(); loadFolders(); }, []);
  useEffect(() => { loadCounts(); loadFolders(); }, [activeFilter, activeLabel]);   // refresh on navigation
  useEffect(() => {
    // 값이 실제로 바뀌었다는 신호다 — 캐시를 버리고 새로 읽어야 숫자가 맞는다.
    // (안 버리면 읽음 처리했는데 안 읽음 개수가 그대로 남는다)
    const h = () => { invalidateMailCache(); loadCounts(); loadLabels(); loadFolders(); };
    window.addEventListener("mail:refresh", h);
    return () => window.removeEventListener("mail:refresh", h);
  }, []);

  // Close the folder context menu on any outside click / scroll / Escape.
  useEffect(() => {
    if (!ctx) return;
    const close = () => setCtx(null);
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setCtx(null); };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("click", close); window.removeEventListener("scroll", close, true); window.removeEventListener("keydown", esc); };
  }, [ctx]);

  const VIRTUAL = new Set(["STARRED", "IMPORTANT", "RECEIVED", "UNREAD"]);
  const afterChange = () => {
    loadFolders(); loadCounts();
    invalidateMailCache();
    window.dispatchEvent(new Event("mail:refresh"));   // sidebar/counts
    window.dispatchEvent(new Event("mail:reload"));     // main list
  };
  const markAllRead = async (folderId: string) => {
    try {
      const r = await api.post<{ affected: number }>(`/mail/mark-all-read?folder=${encodeURIComponent(folderId)}`);
      // 몇 건이 바뀌었는지 알려 준다. 안 읽은 메일이 없으면 눌러도 화면이 그대로라
      // 눌린 건지 알 수 없었다.
      toast.success(r.data.affected ? `${r.data.affected}건 읽음 처리` : "안 읽은 메일이 없습니다");
      afterChange();
    } catch (e: any) { toast.error("실패", e?.response?.data?.error?.message); }
  };
  const emptyMailbox = async (folderId: string, label: string) => {
    const perm = folderId === "TRASH";
    if (!window.confirm(perm ? "휴지통을 비울까요? (영구 삭제)" : `'${label}'의 모든 메일을 휴지통으로 옮길까요?`)) return;
    try { const r = await api.post<{ affected: number }>(`/mail/empty-folder?folder=${encodeURIComponent(folderId)}`); toast.success(`${r.data.affected}건 처리됨`); afterChange(); }
    catch (e: any) { toast.error("비우기 실패", e?.response?.data?.error?.message); }
  };
  const renameLabel = async (l: Label) => {
    const name = window.prompt("라벨 이름 변경", l.name);
    if (name == null || !name.trim() || name.trim() === l.name) return;
    try { await api.patch(`/mail/labels/${l.id}`, { name: name.trim() }); loadLabels(); window.dispatchEvent(new Event("mail:reload")); }
    catch (e: any) { toast.error("이름 변경 실패", e?.response?.data?.error?.message); }
  };
  const deleteLabel = async (l: Label) => {
    if (!window.confirm(`"${l.name}" 라벨을 삭제할까요?`)) return;
    try { await api.delete(`/mail/labels/${l.id}`); loadLabels(); window.dispatchEvent(new Event("mail:reload")); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message); }
  };
  const startAdd = (parentId: number | null) => {
    setAddParentId(parentId);
    if (parentId != null) setExpanded((s) => ({ ...s, [parentId]: true }));
    setOpen((o) => ({ ...o, custom: true }));
    setAdding(true);
  };
  const createFolder = async () => {
    const name = newName.trim();
    if (!name) { setAdding(false); return; }
    try { await api.post("/mail/folders/", { name, parent_id: addParentId }); setNewName(""); setAdding(false); afterChange(); }
    catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message || e?.response?.data?.detail); }
  };
  const renameFolder = async (f: Folder) => {
    const name = window.prompt("메일함 이름 변경", f.name);
    if (name == null || !name.trim() || name.trim() === f.name) return;
    try { await api.patch(`/mail/folders/${f.id}`, { name: name.trim() }); afterChange(); }
    catch (e: any) { toast.error("이름 변경 실패", e?.response?.data?.error?.message || e?.response?.data?.detail); }
  };
  const reorderFolder = async (f: Folder, dir: "up" | "down") => {
    try { await api.patch(`/mail/folders/${f.id}`, { reorder: dir }); afterChange(); }
    catch (e: any) { toast.error("이동 실패", e?.response?.data?.error?.message); }
  };
  const removeFolder = async (f: Folder) => {
    if (!window.confirm(`"${f.name}" 메일함을 삭제할까요? 하위 폴더까지 함께 삭제되고, 안의 메일은 받은 메일함으로 이동됩니다.`)) return;
    try { await api.delete(`/mail/folders/${f.id}`); if (activeFilter === f.code) nav("/mail?filter=RECEIVED"); afterChange(); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message); }
  };

  const toggle = (id: string) => setOpen((o) => {
    const next = { ...o, [id]: !o[id] };
    try { localStorage.setItem("mail-tree-open", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  const go = (q: string) => nav(`/mail?${q}`);

  // 내 메일함 폴더(중첩) 재귀 렌더 — 드래그로 형제 순서 변경, ▶로 하위 접기/펴기
  const renderFolder = (f: Folder, depth: number): React.ReactNode => {
    const kids = childrenOf(f.id);
    const isOpen = expanded[f.id];
    return (
      <div key={f.code}>
        <button
          className={`t-subnav-item ${activeFilter === f.code ? "active" : ""}`}
          style={{ paddingLeft: 8 + depth * 14, opacity: dragId === f.id ? 0.5 : 1 }}
          draggable
          onDragStart={(e) => { setDragId(f.id); e.dataTransfer.effectAllowed = "move"; }}
          onDragOver={(e) => { if (dragId != null && dragId !== f.id && (folders.find((x) => x.id === dragId)?.parent_id ?? null) === (f.parent_id ?? null)) e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); onFolderDrop(f); }}
          onDragEnd={() => setDragId(null)}
          onClick={() => go(`filter=${f.code}`)}
          onContextMenu={(e) => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY, kind: "custom", data: f }); }}
          title="드래그: 순서 변경 · 우클릭: 하위 폴더 · 이름 변경 · 삭제">
          {kids.length > 0 ? (
            <span onClick={(e) => { e.stopPropagation(); toggleExpand(f.id); }}
              style={{ marginRight: 3, fontSize: 9, color: "var(--text-3)", width: 12, display: "inline-block", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s", cursor: "pointer" }}>▶</span>
          ) : <span style={{ width: 12, display: "inline-block", marginRight: 3 }} />}
          <span style={{ marginRight: 5 }}>📂</span>
          <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
          <span className="mail-count" title="안읽음 / 전체">
            <b className={f.unread > 0 ? "u" : ""}>{f.unread}</b><span className="sl">/</span><span className="t">{f.total}</span>
          </span>
        </button>
        {isOpen && kids.map((c) => renderFolder(c, depth + 1))}
        {adding && addParentId === f.id && (
          <div style={{ padding: "4px 8px", paddingLeft: 8 + (depth + 1) * 14 }}>
            <input autoFocus className="t-input" style={{ width: "100%", fontSize: 12, height: 28 }}
              placeholder="하위 폴더 이름 입력 후 Enter" value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") createFolder(); if (e.key === "Escape") { setAdding(false); setNewName(""); } }}
              onBlur={createFolder} />
          </div>
        )}
      </div>
    );
  };

  const header = (id: string, title: string) => (
    <div className="t-subnav-group-title" style={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
      onClick={() => toggle(id)}>
      <span style={{ display: "inline-block", marginRight: 5, fontSize: 9, color: "var(--text-3)", transition: "transform .15s", transform: open[id] ? "rotate(90deg)" : "none" }}>▶</span>
      <span style={{ flex: 1 }}>{title}</span>
    </div>
  );

  return (
    <>
      <div className="t-subnav-header">
        <div>
          <h3>메일</h3>
          <p>메일함 · 라벨</p>
        </div>
        <button className="t-subnav-collapse" onClick={onCollapse} title="접기">
          <I.PanelL size={14} />
        </button>
      </div>

      <div className="t-subnav-body">
        {/* ── 메일함 (system folders) ── */}
        <div className="t-subnav-group">
          {header("folders", "메일함")}
          {open.folders && FOLDERS.map((f) => {
            const active = activeFilter === f.id && !activeLabel;
            const total = counts ? (counts as unknown as Record<string, number>)[f.id] ?? 0 : 0;
            const unread = counts?.unread?.[f.id] ?? 0;
            return (
              <button key={f.id} className={`t-subnav-item ${active ? "active" : ""}`} onClick={() => go(`filter=${f.id}`)}
                onContextMenu={(e) => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY, kind: "system", data: f }); }}
                title="우클릭: 모두 읽음 · 비우기">
                <span style={{ marginRight: 7 }}>{f.icon}</span>
                <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.label}</span>
                <span className="mail-count" title="안읽음 / 전체">
                  <b className={unread > 0 ? "u" : ""}>{unread}</b><span className="sl">/</span><span className="t">{total}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* ── 내 라벨 ── */}
        {labels.length > 0 && (
          <div className="t-subnav-group">
            {header("labels", "내 라벨")}
            {open.labels && labels.map((l) => (
              <button key={l.id} className={`t-subnav-item ${activeLabel === String(l.id) ? "active" : ""}`}
                onClick={() => go(`filter=INBOX&label=${l.id}`)}
                onContextMenu={(e) => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY, kind: "label", data: l }); }}
                title="우클릭: 이름 변경 · 삭제">
                <span style={{ width: 10, height: 10, borderRadius: 3, background: l.color, marginRight: 8, flexShrink: 0, display: "inline-block" }} />
                <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* ── 내 메일함 (custom folders) ── */}
        <div className="t-subnav-group">
          <div className="t-subnav-group-title" style={{ display: "flex", alignItems: "center", userSelect: "none" }}>
            <span onClick={() => toggle("custom")} style={{ display: "inline-block", marginRight: 5, fontSize: 9, color: "var(--text-3)", transition: "transform .15s", transform: open.custom ? "rotate(90deg)" : "none", cursor: "pointer" }}>▶</span>
            <span style={{ flex: 1, cursor: "pointer" }} onClick={() => toggle("custom")}>내 메일함</span>
            <button title="메일함 추가" onClick={(e) => { e.stopPropagation(); startAdd(null); }}
              style={{ background: "transparent", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "0 4px" }}>＋</button>
          </div>
          {open.custom && (
            <>
              {childrenOf(null).map((f) => renderFolder(f, 0))}
              {adding && addParentId == null && (
                <div style={{ padding: "4px 8px" }}>
                  <input autoFocus className="t-input" style={{ width: "100%", fontSize: 12, height: 28 }}
                    placeholder="메일함 이름 입력 후 Enter" value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") createFolder(); if (e.key === "Escape") { setAdding(false); setNewName(""); } }}
                    onBlur={createFolder}/>
                </div>
              )}
              {folders.length === 0 && !adding && (
                <div style={{ padding: "4px 12px 4px 24px", fontSize: 12, color: "var(--text-3)" }}>＋ 로 메일함을 추가하세요</div>
              )}
            </>
          )}
        </div>

        {/* ── 외부 메일 ── */}
        <div className="t-subnav-group">
          {header("ext", "외부 메일")}
          {open.ext && (
            <>
              <button className={`t-subnav-item ${activeFilter === "ACCOUNTS" ? "active" : ""}`} onClick={() => go("filter=ACCOUNTS")}>
                <span style={{ marginRight: 7 }}>📧</span>
                <span style={{ flex: 1, textAlign: "left" }}>IMAP 계정 연결</span>
              </button>
              <button className={`t-subnav-item ${activeFilter === "SHARED" ? "active" : ""}`} onClick={() => go("filter=SHARED")}>
                <span style={{ marginRight: 7 }}>👥</span>
                <span style={{ flex: 1, textAlign: "left" }}>공용 편지함</span>
              </button>
            </>
          )}
        </div>

        {/* ── 주소록 ── */}
        <button className={`t-subnav-item ${activeFilter === "CONTACTS" ? "active" : ""}`} onClick={() => go("filter=CONTACTS")}>
          <span style={{ marginRight: 7 }}>📇</span>
          <span style={{ flex: 1, textAlign: "left" }}>주소록</span>
        </button>

        {/* ── 최하단: 메일함 환경설정 ── */}
        <button className={`t-subnav-item ${activeFilter === "SETTINGS" ? "active" : ""}`}
          onClick={() => go("filter=SETTINGS")}
          style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 12, borderRadius: 0 }}>
          <span style={{ marginRight: 7 }}>⚙️</span>
          <span style={{ flex: 1, textAlign: "left" }}>메일함 환경설정</span>
        </button>
      </div>

      {/* 서브메뉴 우클릭 컨텍스트 메뉴 (시스템 메일함 · 라벨 · 내 메일함) */}
      {ctx && (() => {
        const mkSystem = (f: { id: string; label: string }) => {
          const its: { label: string; fn: () => void; danger?: boolean }[] = [
            { label: "📂 열기", fn: () => go(`filter=${f.id}`) },
          ];
          // 가상 폴더(전체·안읽음·별표·중요)도 '모두 읽음'은 된다 — 서버 mark_all_read 가
          // 각 가상 폴더의 범위를 목록과 같은 규칙으로 처리한다. 반면 '비우기'는 막아 둔다:
          // 가상 폴더는 실제 보관 위치가 아니라서, 비운다는 게 무슨 뜻인지 정의되지 않는다.
          its.push({ label: "✓ 모두 읽음 표시", fn: () => markAllRead(f.id) });
          its.push({ label: "↻ 새로고침", fn: () => { loadCounts(); window.dispatchEvent(new Event("mail:reload")); } });
          if (!VIRTUAL.has(f.id)) its.push({ label: f.id === "TRASH" ? "🧹 휴지통 비우기(영구삭제)" : "🧹 메일함 비우기", fn: () => emptyMailbox(f.id, f.label), danger: true });
          return its;
        };
        const mkLabel = (l: Label) => [
          { label: "📂 열기", fn: () => go(`filter=INBOX&label=${l.id}`) },
          { label: "✎ 이름 변경", fn: () => renameLabel(l) },
          { label: "🗑 삭제", fn: () => deleteLabel(l), danger: true },
        ];
        const mkCustom = (f: Folder) => [
          { label: "＋ 하위 폴더 추가", fn: () => startAdd(f.id) },
          { label: "＋ 최상위 메일함 추가", fn: () => startAdd(null) },
          { label: "✎ 이름 변경", fn: () => renameFolder(f) },
          { label: "↑ 위로 이동", fn: () => reorderFolder(f, "up") },
          { label: "↓ 아래로 이동", fn: () => reorderFolder(f, "down") },
          { label: "✓ 모두 읽음 표시", fn: () => markAllRead(f.code) },
          { label: "🧹 메일함 비우기", fn: () => emptyMailbox(f.code, f.name) },
          { label: "🗑 삭제(하위 포함)", fn: () => removeFolder(f), danger: true },
        ];
        const items = (ctx.kind === "system" ? mkSystem(ctx.data) : ctx.kind === "label" ? mkLabel(ctx.data) : mkCustom(ctx.data)) as { label: string; fn: () => void; danger?: boolean }[];
        return (
          <div onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()}
            style={{ position: "fixed", left: ctx.x, top: ctx.y, zIndex: 1000, minWidth: 168, background: "var(--surface,#fff)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 10px 36px rgba(0,0,0,.2)", padding: 4 }}>
            {items.map((it) => (
              <button key={it.label} onClick={() => { setCtx(null); it.fn(); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 10px", border: "none", background: "transparent", borderRadius: 6, fontSize: 13, cursor: "pointer", color: it.danger ? "var(--danger,#ef4444)" : "var(--text-1)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                {it.label}
              </button>
            ))}
          </div>
        );
      })()}
    </>
  );
}
